// Copyright ©️ 2026 eVolve MEP, LLC

import { useEffect } from 'react';

import {
  useEvolveDelete,
  entityAttachmentUrlSegments,
  type UploadableEntityAttachmentType,
  type EntityAttachment,
  type WithOptional,
} from '@evolvemep/api-ts';
import { Text, Stack, getDefaultZIndex } from '@mantine/core';
import { notifications } from '@mantine/notifications';

import { ModalButtons } from 'components/Mantine/Modals/ModalButtons';
import { VariableTrackingModal } from 'components/Mantine/Modals/VariableTrackingModal';
import { useWrappedModal } from 'components/Mantine/Modals/WrappedModal';

type Props<T extends UploadableEntityAttachmentType> = {
  entityType: T;
  attachment: EntityAttachment<T>;
  refresh: () => void;
  onClose: () => void;
  onAttachmentDeleted?: () => void;
};

const DeleteAttachmentForm = <T extends UploadableEntityAttachmentType>({
  entityType,
  attachment,
  refresh,
  onClose,
  onAttachmentDeleted,
}: Props<T>) => {
  const { apiCall: deleteAttachmentApiCall, loading } = useEvolveDelete(
    'file' in attachment
      ? `shop/${entityAttachmentUrlSegments[entityType]}/fileAttachment/${attachment[`${entityType}FileAttachmentId`]}`
      : `shop/${entityAttachmentUrlSegments[entityType]}/webLinkAttachment/${attachment[`${entityType}WebLinkAttachmentId`]}`,
  );

  const attachmentType = 'webLink' in attachment ? 'URL attachment' : 'file attachment';

  const { setLoading } = useWrappedModal();

  useEffect(() => {
    setLoading(loading);
  }, [setLoading, loading]);

  const onConfirm = async () => {
    // I can't make this type happy, as the call to `apiCall` is becoming too type-expanded
    // oxlint-disable-next-line typescript/no-explicit-any
    await deleteAttachmentApiCall(...([] as any));
    notifications.show({
      title: 'Successfully deleted',
      message: `Deleted ${attachmentType}.`,
      color: 'green',
    });
    refresh();
    onAttachmentDeleted?.();
    onClose();
  };

  return (
    <Stack gap={0}>
      <Text p="md">Are you sure you want to delete this {attachmentType}?</Text>
      <ModalButtons onConfirm={onConfirm} onClose={onClose} confirmationText="Delete" />
    </Stack>
  );
};

export const DeleteAttachmentModal = <T extends UploadableEntityAttachmentType>(
  props: WithOptional<Props<T>, 'attachment'>,
) => (
  <VariableTrackingModal
    title="Delete attachment"
    variable={props.attachment}
    onConfirm={props.onClose}
    onClose={props.onClose}
    confirmationText="Delete"
    bodyProps={{ p: 0 }}
    hideModalButtons
    // This Modal is often rendered inside of an `AttachmentsPopover`
    zIndex={getDefaultZIndex('popover') + 1}
  >
    {(attachment) => <DeleteAttachmentForm {...props} attachment={attachment} />}
  </VariableTrackingModal>
);
